import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import ImageScroller from '../../components/imagescroller';
import Placeholder from '../../components/gui/placeholder';
import MarkdownDisplay from '../../components/gui/markdownDisplay';
import ShareButton from '../share';

import localUtil from '../../lib/util';
import { has } from 'mobx';

const REACTION_TYPES = [
    { type: 'like', icon: 'fa-thumbs-up', color: '#1877f2', label: 'Like' },
    { type: 'love', icon: 'fa-heart', color: '#e74c3c', label: 'Love' },
    { type: 'haha', icon: 'fa-face-laugh-squint', color: '#f1c40f', label: 'Haha' },
    { type: 'wow', icon: 'fa-face-surprise', color: '#f1c40f', label: 'Wow' },
    { type: 'sad', icon: 'fa-face-sad-tear', color: '#f1c40f', label: 'Sad' },
    { type: 'angry', icon: 'fa-face-angry', color: '#e67e22', label: 'Angry' },
    { type: 'stars', icon: 'fa-face-grin-stars', color: '#f1c40f', label: 'Stars' },
    { type: 'think', icon: 'fa-face-thinking', color: '#f1c40f', label: 'Think' },
    { type: 'fire', icon: 'fa-fire-flame-curved', color: '#e67e22', label: 'Fire' },
];

@observer
class Likes extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            hasLiked: false,
            likes: null,
            showReactionPicker: false,
            userReactionType: null,
        };
        this.longPressTimer = null;
        this.longPressTriggered = false;
    }

    loadAll = async (props = this.props) => {
        const { likes = [] } = props;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const userLike = likes.find(like => like.user === user.id);
        if (userLike) {
            this.setState({
                hasLiked: true,
                userReactionType: userLike.likeType || 'like',
            });
        } else {
            this.setState({
                hasLiked: false,
                userReactionType: null,
            });
        }
    }

    addLike = async (likeType = 'like') => {
        const { type, objectId } = this.props;
        const { likeStore } = this.props.stores;
        await likeStore.insert({ type, objectId, likeType });
        this.setState({
            hasLiked: true,
            userReactionType: likeType,
            showReactionPicker: false,
        });

        const likes = await likeStore.load({
            query: { type, objectId },
            skipUpdate: true,
        });
        this.setState({ likes, justLiked: true });
        setTimeout(() => {
            this.setState({ justLiked: false });
        }, 800);
    }

    changeReaction = async (likeType) => {
        const { type, objectId } = this.props;
        const { likeStore } = this.props.stores;
        await likeStore.changeReaction({ type, objectId, likeType });
        this.setState({
            userReactionType: likeType,
            showReactionPicker: false,
        });

        const likes = await likeStore.load({
            query: { type, objectId },
            skipUpdate: true,
        });
        this.setState({ likes });
    }

    removeLike = async () => {
        const { type, objectId } = this.props;
        const { likeStore } = this.props.stores;
        await likeStore.delete(1, 'id', { type, objectId });
        this.setState({
            hasLiked: false,
            userReactionType: null,
            showReactionPicker: false,
        });

        const likes = await likeStore.load({
            query: { type, objectId },
            skipUpdate: true,
        });
        this.setState({ likes });
    }

    selectReaction = async (reactionType) => {
        const { hasLiked, userReactionType } = this.state;

        if (hasLiked) {
            if (reactionType === userReactionType) {
                // Same reaction - remove like
                await this.removeLike();
            } else {
                // Different reaction - change it
                await this.changeReaction(reactionType);
            }
        } else {
            // New like
            await this.addLike(reactionType);
        }
    }

    handleLikeClick = async (e) => {
        e.stopPropagation();
        if (this.longPressTriggered) {
            this.longPressTriggered = false;
            return;
        }

        const { hasLiked, userReactionType } = this.state;
        if (hasLiked) {
            await this.removeLike();
        } else {
            await this.addLike('like');
        }
    }

    handleTouchStart = (e) => {
        this.longPressTriggered = false;
        this.touchStartX = e.touches?.[0]?.clientX || 0;
        this.touchStartY = e.touches?.[0]?.clientY || 0;
        this.longPressTimer = setTimeout(() => {
            this.longPressTriggered = true;
            this.setState({ showReactionPicker: true });
            // Add vibration feedback if available
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }
        }, 400);
    }

    handleTouchMove = (e) => {
        // Cancel long press if user moves finger (scrolling)
        if (this.longPressTimer) {
            const moveX = Math.abs((e.touches?.[0]?.clientX || 0) - this.touchStartX);
            const moveY = Math.abs((e.touches?.[0]?.clientY || 0) - this.touchStartY);
            if (moveX > 10 || moveY > 10) {
                clearTimeout(this.longPressTimer);
                this.longPressTimer = null;
            }
        }
    }

    handleTouchEnd = (e) => {
        if (this.longPressTimer) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
    }

    handleMouseEnter = () => {
        // Only use hover on non-touch devices
        if ('ontouchstart' in window) return;
        this.cancelHideTimer();
        this.hoverTimer = setTimeout(() => {
            this.setState({ showReactionPicker: true });
        }, 500);
    }

    handleMouseLeave = () => {
        // Only use hover on non-touch devices
        if ('ontouchstart' in window) return;
        if (this.hoverTimer) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = null;
        }
        this.hideTimer = setTimeout(() => {
            this.setState({ showReactionPicker: false });
        }, 800);
    }

    cancelHideTimer = () => {
        if (this.hideTimer) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }
    }

    hideReactionPicker = () => {
        this.setState({ showReactionPicker: false });
    }

    handleOutsideClick = (e) => {
        // Close picker when clicking outside
        if (this.pickerRef && !this.pickerRef.contains(e.target)) {
            this.setState({ showReactionPicker: false });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Add/remove click outside listener when picker opens/closes
        if (this.state.showReactionPicker && !prevState.showReactionPicker) {
            document.addEventListener('click', this.handleOutsideClick, true);
            document.addEventListener('touchstart', this.handleOutsideClick, true);
        } else if (!this.state.showReactionPicker && prevState.showReactionPicker) {
            document.removeEventListener('click', this.handleOutsideClick, true);
            document.removeEventListener('touchstart', this.handleOutsideClick, true);
        }
    }

    toggleComment = async () => {
        const { commentStore } = this.props.stores;
        const { showAddComment = {} } = commentStore;
        const { type, objectId } = this.props;

        const addComment = { ...showAddComment };
        addComment[`${type}-${objectId}`] = !addComment[`${type}-${objectId}`];
        commentStore.updateKeyValue('showAddComment', addComment);
    }

    gotoLogin = () => {
        route('/login');
    }

    getReactionIcon = () => {
        const { userReactionType } = this.state;
        return REACTION_TYPES.find(r => r.type === userReactionType) || REACTION_TYPES[0];
    }

    getReactionCounts = (likes) => {
        return likes.reduce((acc, like) => {
            const type = like.likeType || 'like';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});
    }

    renderReactionPicker = () => {
        const { showReactionPicker } = this.state;
        const { alignRight } = this.props;
        if (!showReactionPicker) return null;

        const positionStyle = alignRight
            ? 'bottom: 100%; right: 0; z-index: 1000; animation: fadeInUp 0.2s ease;'
            : 'bottom: 100%; left: 0; z-index: 1000; animation: fadeInUp 0.2s ease;';

        return (
            <div
                ref={el => this.pickerRef = el}
                class='reaction-picker position-absolute d-flex flex-row bg-body rounded-pill shadow-lg p-1 border'
                style={positionStyle}
                onMouseEnter={() => { this.cancelHideTimer(); if (this.hoverTimer) clearTimeout(this.hoverTimer); }}
                onMouseLeave={this.handleMouseLeave}
            >
                {REACTION_TYPES.map(reaction => (
                    <button
                        key={reaction.type}
                        class='btn btn-link p-1 px-2 reaction-btn'
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); this.selectReaction(reaction.type); }}
                        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); this.selectReaction(reaction.type); }}
                        title={reaction.label}
                        style='transition: transform 0.1s;'
                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.3)'}
                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                        <i class={`fa-solid ${reaction.icon}`} style={`color: ${reaction.color}; font-size: 1.2em;`} />
                    </button>
                ))}
            </div>
        );
    }

    renderReactionSummary = (likes) => {
        const reactionCounts = this.getReactionCounts(likes);
        const topReactions = Object.entries(reactionCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3);

        if (topReactions.length === 0) {
            return <span>{likes.length} <Text id='like.likes'>Likes</Text></span>;
        }

        return (
            <div class='d-flex align-items-center'>
                {topReactions.map(([type]) => {
                    const reaction = REACTION_TYPES.find(r => r.type === type);
                    if (!reaction) return null;
                    return (
                        <i
                            key={type}
                            class={`fa-solid ${reaction.icon} me-1`}
                            style={`color: ${reaction.color}; font-size: 0.9em;`}
                        />
                    );
                })}
                <span class='ms-1'>{likes.length}</span>
            </div>
        );
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.likes !== this.props.likes) {
            // Fresh likes from a feed refresh replace the copy cached by the
            // last user action, so other members' reactions show up.
            this.setState({ likes: null });
            this.loadAll(nextProps);
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillUnmount() {
        if (this.longPressTimer) clearTimeout(this.longPressTimer);
        if (this.hoverTimer) clearTimeout(this.hoverTimer);
        if (this.hideTimer) clearTimeout(this.hideTimer);
        document.removeEventListener('click', this.handleOutsideClick, true);
        document.removeEventListener('touchstart', this.handleOutsideClick, true);
    }

    render() {
        const { hasLiked, justLiked, likes: localLikes, showReactionPicker, userReactionType } = this.state;
        const {
            likes = [],
            inputLikes = [],
            type,
            objectId,
            uuidv4,
            showComment = true,
            showSimpleLike = false,
            showShare = false,
            className = 'likes',
            shareTitle,
            shareSummary,
            shareImage,
        } = this.props;
        const { appState, userStore, commentStore } = this.props.stores;
        const { user, isBlockedFromComments } = userStore;
        const isLoggedIn = user.id;
        const canLike = isLoggedIn && !isBlockedFromComments;

        const { showAddComment } = commentStore;

        const finalLikes = (localLikes || likes).concat(inputLikes || []).filter(like => like.objectId == objectId);
        const finalHasLiked = hasLiked || finalLikes.find(like => like.user === user.id && like.objectId == objectId);

        const currentReaction = this.getReactionIcon();
        const reactionStyle = finalHasLiked ? `color: ${currentReaction.color};` : '';

        if (showSimpleLike) {
            return (<>
                {canLike ? <>
                    <small
                        class={`text-muted ${className}`}
                        onClick={this.handleLikeClick}
                        onTouchStart={this.handleTouchStart}
                        onTouchMove={this.handleTouchMove}
                        onTouchEnd={this.handleTouchEnd}
                        onMouseEnter={this.handleMouseEnter}
                        onMouseLeave={this.handleMouseLeave}
                        style='position: relative; cursor: pointer;'
                    >
                        {this.renderReactionPicker()}
                        <i
                            class={`fa-${finalHasLiked ? 'solid' : 'light'} ${finalHasLiked ? currentReaction.icon : 'fa-thumbs-up'} ${justLiked ? 'heart animate' : ''}`}
                            style={reactionStyle}
                        ></i> <span class='ms-1' style={reactionStyle}>
                            {finalHasLiked && userReactionType ? currentReaction.label : <Text id='like.like'>Like</Text>}
                        </span>
                    </small>
                </> : !isBlockedFromComments && <>
                    <small class={`text-muted ${className}`}>
                        <button class='btn btn-link p-0' onClick={this.gotoLogin}><Text id='like.loginToLike'>Login to like</Text></button>
                    </small>
                </>}
            </>);
        }

        return (<>
            <div class='w-100 d-flex flex-row justify-content-between position-relative text-muted px-3 py-2' style='z-index: 10;'>
                <div class='d-flex flex-row likes'>
                    {canLike ? <>
                        <div
                            class='position-relative'
                            onMouseEnter={this.handleMouseEnter}
                            onMouseLeave={this.handleMouseLeave}
                        >
                            {this.renderReactionPicker()}
                            <button
                                class='btn btn-link p-0 likes'
                                style={reactionStyle || 'color: #6c757d;'}
                                onClick={this.handleLikeClick}
                                onTouchStart={this.handleTouchStart}
                                onTouchMove={this.handleTouchMove}
                                onTouchEnd={this.handleTouchEnd}
                            >
                                <i
                                    class={`fa-${finalHasLiked ? 'solid' : 'light'} ${finalHasLiked ? currentReaction.icon : 'fa-thumbs-up'} ${justLiked ? 'heart animate' : ''}`}
                                ></i> <span class='ms-1'>
                                    {finalHasLiked && userReactionType ? currentReaction.label : <Text id='like.like'>Like</Text>}
                                </span>
                            </button>
                        </div>
                        {showComment && <button
                            class='btn btn-link p-0 text-muted ms-4 likes'
                            onClick={this.toggleComment}
                        >
                            <i
                                class={`fa-${showAddComment[`${type}-${objectId}`] ? 'solid' : 'light'} fa-comment`}
                                onClick={this.toggleComment}
                            ></i> <span class='ms-1'><Text id='like.comment'>Comment</Text></span>
                        </button>}
                    </> : !isBlockedFromComments && <>
                        <small class='likes'>
                            <button class='btn btn-link p-0' onClick={this.gotoLogin}><Text id='like.loginToLike'>Login to like</Text></button>
                        </small>
                    </>}

                    <ShareButton
                        type={type}
                        uuidv4={uuidv4}
                        title={shareTitle}
                        summary={shareSummary}
                        image={shareImage}
                    />

                    {this.props.children && <div class='ms-4 d-flex flex-row align-items-center gap-3'>
                        {this.props.children}
                    </div>}

                </div>
                <div class='d-flex flex-row font-weight-light likes'>
                    {this.renderReactionSummary(finalLikes)}
                </div>
            </div>
        </>);
    }
}

export default Likes;
